import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  App as AntdApp,
  Button,
  Flex,
  Popconfirm,
  Select,
  Tag,
  Typography
} from 'antd'
import type { TableColumnsType } from 'antd'
import { Check, X } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'

import {
  organizationsJoinRequestsApproveCreateMutation,
  organizationsJoinRequestsListOptions,
  organizationsJoinRequestsListQueryKey,
  organizationsJoinRequestsRejectCreateMutation,
  OrganizationJoinRequestStatusEnum,
  type OrganizationJoinRequest
} from 'src/api/main'
import {
  ActiveFilterTags,
  BadgeText,
  DateTimeCell,
  ProTableContainer,
  QueryErrorAlert,
  TableActionColumnTitle
} from 'src/components'
import { FILTER_CONTROL_WIDTH } from 'src/constants'
import { FilterLabelsProvider, useFilterLabels } from 'src/context'
import {
  useHasWorkspacePermissions,
  useQueryParams,
  useQueryUpdate
} from 'src/hooks'
import {
  defaultFilters,
  normalizeListResults,
  useShowApiErrors,
  type ActiveFilterConfig,
  WorkspacePermissionCode
} from 'src/utils'

const { Text } = Typography

const STATUS_VALUES = [
  OrganizationJoinRequestStatusEnum.PENDING,
  OrganizationJoinRequestStatusEnum.APPROVED,
  OrganizationJoinRequestStatusEnum.REJECTED
] as const

const STATUS_COLOR: Record<OrganizationJoinRequestStatusEnum, string> = {
  [OrganizationJoinRequestStatusEnum.PENDING]: 'processing',
  [OrganizationJoinRequestStatusEnum.APPROVED]: 'success',
  [OrganizationJoinRequestStatusEnum.REJECTED]: 'default'
}

/** The `STATUS_COLOR` presets above, as the raw dot colour `BadgeText` needs. */
const STATUS_DOT: Record<OrganizationJoinRequestStatusEnum, string> = {
  [OrganizationJoinRequestStatusEnum.PENDING]: 'var(--ant-color-info)',
  [OrganizationJoinRequestStatusEnum.APPROVED]: 'var(--ant-color-success)',
  [OrganizationJoinRequestStatusEnum.REJECTED]:
    'var(--ant-color-text-quaternary)'
}

/**
 * People asking to join this workspace because their email domain already
 * belongs to it (BR-10).
 *
 * Asking grants nothing — the platform files the request and notifies the
 * administrators, and this is where they answer. Approval carries no role:
 * the endpoint takes no body, and the new member is assigned one from their
 * member page afterwards.
 */
const WorkspaceJoinRequestsPageInner = () => {
  const { t } = useTranslation()
  const { message } = AntdApp.useApp()
  const queryClient = useQueryClient()
  const showApiErrors = useShowApiErrors()
  const { id: idParam = '' } = useParams<{ id: string }>()
  const organizationId = Number(idParam)

  const params = useQueryParams()
  const { updateQuery } = useQueryUpdate()
  const filterLabels = useFilterLabels()

  const page = Number(params.page) || defaultFilters.page
  const perPage =
    Number(params.page_size ?? params.per_page) || defaultFilters.per_page
  const searchTerm = String(params.search ?? '')
  const statusFilter = params.status as
    | OrganizationJoinRequestStatusEnum
    | undefined
  const isDirty = searchTerm !== '' || Boolean(statusFilter)

  const setFilter = (patch: Record<string, unknown>) =>
    updateQuery({ ...params, page: defaultFilters.page, ...patch })

  const statusOptions = useMemo(
    () =>
      STATUS_VALUES.map((value) => ({
        value,
        // `title` carries the plain string: the label is a node, and the chip
        // registry below can only read a string off one of the two.
        title: t(`users.joinRequests.status.${value}`),
        label: (
          <BadgeText
            text={t(`users.joinRequests.status.${value}`)}
            color={STATUS_DOT[value]}
          />
        )
      })),
    [t]
  )

  // Static options, so the chips row needs them registered by hand — an
  // API-backed `InfiniteSelect` filter would do this itself via `filterKey`.
  useEffect(() => {
    filterLabels?.registerOptions('status', statusOptions)
  }, [filterLabels, statusOptions])

  const filterTagItems: ActiveFilterConfig[] = [
    {
      type: 'select',
      key: 'status',
      label: t('users.joinRequests.columns.status')
    }
  ]

  /** The row whose decision is in flight, so only its buttons spin. */
  const [deciding, setDeciding] = useState<number | null>(null)
  const canReviewRequests = useHasWorkspacePermissions(
    WorkspacePermissionCode.USERS_INVITE
  )

  const { data, isFetching, isError, refetch } = useQuery(
    organizationsJoinRequestsListOptions({
      path: { organization_id: organizationId },
      query: {
        page,
        per_page: perPage,
        ...(searchTerm ? { search: searchTerm } : {}),
        ...(statusFilter ? { status: statusFilter } : {})
      }
    })
  )

  const { mutateAsync: approve } = useMutation(
    organizationsJoinRequestsApproveCreateMutation()
  )
  const { mutateAsync: reject } = useMutation(
    organizationsJoinRequestsRejectCreateMutation()
  )

  const refreshList = () =>
    queryClient.invalidateQueries({
      queryKey: organizationsJoinRequestsListQueryKey({
        path: { organization_id: organizationId }
      })
    })

  const decide = async (
    request: OrganizationJoinRequest,
    verdict: 'approve' | 'reject'
  ) => {
    setDeciding(request.id)
    try {
      if (verdict === 'approve') {
        // Approval takes no body: the tier it used to carry is `readonly` on
        // the access record, and roles are assigned from the member page.
        await approve({
          path: { id: request.id, organization_id: organizationId }
        })
        message.success(
          t('users.joinRequests.approved', { email: request.email })
        )
      } else {
        await reject({
          path: { id: request.id, organization_id: organizationId }
        })
        message.success(
          t('users.joinRequests.rejected', { email: request.email })
        )
      }
      await refreshList()
    } catch (error) {
      showApiErrors(error)
    } finally {
      setDeciding(null)
    }
  }

  // Annotated as paginated, served as a bare array — accept both shapes, or the
  // table renders "No data" over a response that carried rows.
  const rows = normalizeListResults(data)
  const total = Array.isArray(data) ? rows.length : (data?.count ?? 0)

  const columns: TableColumnsType<OrganizationJoinRequest> = [
    {
      title: t('users.joinRequests.columns.person'),
      key: 'person',
      fixed: 'left',
      render: (_, request) => (
        <Flex vertical>
          <Text>{request.user.full_name || request.email}</Text>
          {request.user.full_name ? (
            <Text type="secondary">{request.email}</Text>
          ) : null}
        </Flex>
      )
    },
    {
      title: t('users.joinRequests.columns.requested'),
      key: 'date_created',
      dataIndex: 'date_created',
      width: 170,
      render: (value: string) => <DateTimeCell value={value} variant="auto" />
    },
    {
      title: t('users.joinRequests.columns.status'),
      key: 'status',
      dataIndex: 'status',
      width: 130,
      render: (status: OrganizationJoinRequestStatusEnum) => (
        <Tag color={STATUS_COLOR[status]}>
          {t(`users.joinRequests.status.${status}`)}
        </Tag>
      )
    },
    {
      title: t('users.joinRequests.columns.reviewed'),
      key: 'reviewed_at',
      dataIndex: 'reviewed_at',
      width: 170,
      render: (value: string | null) => (
        <DateTimeCell value={value} variant="auto" />
      )
    },
    {
      title: <TableActionColumnTitle />,
      key: 'actions',
      width: 190,
      render: (_, request) =>
        canReviewRequests &&
        request.status === OrganizationJoinRequestStatusEnum.PENDING ? (
          <Flex gap={8}>
            <Popconfirm
              title={t('users.joinRequests.approveConfirm', {
                email: request.email
              })}
              okText={t('users.joinRequests.approve')}
              cancelText={t('cancel')}
              onConfirm={() => void decide(request, 'approve')}
            >
              <Button
                size="small"
                type="primary"
                icon={<Check size={14} />}
                loading={deciding === request.id}
              >
                {t('users.joinRequests.approve')}
              </Button>
            </Popconfirm>
            <Popconfirm
              title={t('users.joinRequests.rejectConfirm', {
                email: request.email
              })}
              okText={t('users.joinRequests.reject')}
              okButtonProps={{ danger: true }}
              cancelText={t('cancel')}
              onConfirm={() => void decide(request, 'reject')}
            >
              <Button
                size="small"
                danger
                icon={<X size={14} />}
                disabled={deciding === request.id}
              >
                {t('users.joinRequests.reject')}
              </Button>
            </Popconfirm>
          </Flex>
        ) : null
    }
  ]

  if (isError) return <QueryErrorAlert onRetry={() => void refetch()} />

  return (
    <ProTableContainer<OrganizationJoinRequest>
      tableIdentifier="workspace-join-requests"
      searchPlaceholder={t('users.joinRequests.searchPlaceholder')}
      rowKey="id"
      columns={columns}
      data={{ count: total, results: rows }}
      loading={isFetching}
      leftChildren={
        <>
          <Select<OrganizationJoinRequestStatusEnum>
            size="small"
            allowClear
            placeholder={t('users.joinRequests.columns.status')}
            value={statusFilter}
            onChange={(value) => setFilter({ status: value ?? null })}
            style={{ width: FILTER_CONTROL_WIDTH }}
            options={statusOptions}
          />

          {isDirty && (
            <Flex flex="1 0 100%">
              <ActiveFilterTags
                items={filterTagItems}
                onClearAll={() => updateQuery({}, true)}
              />
            </Flex>
          )}
        </>
      }
    />
  )
}

export const WorkspaceJoinRequestsPage = () => (
  <FilterLabelsProvider>
    <WorkspaceJoinRequestsPageInner />
  </FilterLabelsProvider>
)
